import {ConditionClassMap, OperatorType} from "./ConditionClassMap";
import {evaluateTemplate} from "../../TemplateEvaluation";
import {NOOP_FUNCTION, toArray} from "../../../../stem-core/base/Utils.js";
import {userDataModule} from "../../UserDataModule.js";
import {JourneyActionRunner} from "./JourneyActionRunner.js";
import {merchantFunctionModule} from "../../merchant-state/MerchantFunctionModule.js";
import {Condition, type Journey} from "./Condition";
import {CleanupJobs} from "../../../../stem-core/base/Dispatcher.js";
import {CallThrottler} from "../../../../stem-core/base/CallModifier";
import {onCurrentAndFuturePageAccess} from "../../../utils/PageAccessHelpers.js";
import {type PanelPlacement} from "../../PanelModule";


// A condition node can be a string (condition type name), an array (implicit AND),
// or an object with a type field and condition-specific options.
type ConditionNodeDescription = string | ConditionNodeDescription[] | {
    type: string;
    exclude?: boolean;
    ops?: ConditionNodeDescription[];
    op?: ConditionNodeDescription;
};

export function parseConditionNode(journey: Journey, node: ConditionNodeDescription, requestReEval: () => void): Condition {
    // Array is implicitly treated as an "and" operator.
    if (Array.isArray(node)) {
        node = {type: OperatorType.AND, ops: node};
    }

    // A node with the exclude key set is negated.
    if (typeof node === "object" && node.exclude) {
        node = {type: OperatorType.NOT, op: {...node, exclude: undefined}};
    }

    // A string node is considered a condition of that type.
    if (typeof node === "string" || node instanceof String) {
        node = {type: node as string};
    }

    const conditionClass = ConditionClassMap[node.type];
    if (!conditionClass) {
        throw `Unknown condition type: ${node.type}`;
    }
    const condition: Condition = new conditionClass(journey, node, requestReEval);
    if (node.type === OperatorType.AND || node.type === OperatorType.OR) {
        condition.ops = node.ops.map((subCondition: ConditionNodeDescription) => parseConditionNode(journey, subCondition, requestReEval));
    }
    if (node.type === OperatorType.NOT) {
        condition.ops = [parseConditionNode(journey, node.op, requestReEval)];
    }
    return condition;
}


// Will evaluate the journey condition now and whenever needed and will call the runner
export class JourneyConditionEvaluator {
    journey: Journey;
    runner: JourneyActionRunner;
    condition: Condition;
    // What the actions put up, taken down when the condition flips back to false (e.g. the buyer navigates to a
    // non-gated page in an SPA). A placement replaces a discarded gate by itself, a single one after asking regate().
    placements: PanelPlacement[] = [];
    // Whether the actions have already run for the current stretch of the condition being true. Actions
    // that put up nothing (popups, callbacks) leave no placement behind, so without this they'd fire again
    // on every navigation.
    haveRunWhileTrue = false;
    // Set by a login after the actions ran: if the condition still holds, they run again.
    rerunPending = false;
    // Set when a panel went back up after the router discarded it: if the condition still holds, the other actions
    // run again, as they did along with it the first time.
    regatePending = false;
    cleanupJobs = new CleanupJobs();
    evaluateDebounced: () => void;

    constructor(journey: Journey) {
        this.journey = journey;
        this.runner = new JourneyActionRunner(journey, this);
        // Cancelled on cleanup, so an evaluation already queued can't run the actions once we're gone
        const evaluateThrottler = new CallThrottler({debounce: 0});
        this.cleanupJobs.add(evaluateThrottler);
        this.evaluateDebounced = evaluateThrottler.wrap(() => this.evaluate()); // TODO @branch this shouldn't fail just because this needs to be async

        // TODO @cleanup remove duplicate code
        const evaluatedConditionOptions = evaluateTemplate(journey.options.condition || "true");
        this.condition = parseConditionNode(journey, evaluatedConditionOptions, this.evaluateDebounced);
        this.evaluate();

        // Re-evaluate on every SPA navigation so path-based journeys (not just nth-page ones) get a
        // chance to tear down a gate that no longer applies on the new route.
        this.cleanupJobs.add(onCurrentAndFuturePageAccess(this.evaluateDebounced));

        for (const eventType of toArray(journey.options?.reeval)) {
            if (eventType === "auth") {
                this.cleanupJobs.add(userDataModule.authenticationChange.addListener(() => {
                    // TODO This is really complicated because that fucking templating is only evaluated once and should be dynamic
                    this.condition?.cleanup();
                    const evaluatedConditionOptions = evaluateTemplate(journey.options.condition || "true");
                    this.condition = parseConditionNode(journey, evaluatedConditionOptions, this.evaluateDebounced);
                    // Only a condition that's now false takes down everything, since the buyer may be logging in
                    // through one of our panels. Otherwise the actions run again, for templates that read the login
                    // state.
                    this.rerunPending = this.haveRunWhileTrue;
                    this.evaluate();
                }));
            } else {
                console.warn("Unknown reeval condition", eventType);
            }
        }
    }

    evaluate(): void {
        const result = this.condition.evaluate();

        // Null means "not yet known" (a pending merchant promise, say), which must not remove an
        // already-shown gate. Anything else falsy is a real no, whatever its type - the conditions
        // aren't all strict about returning booleans.
        if (result == null) {
            return;
        }

        if (!result) {
            this.removePlacements();
            return;
        }

        if (this.haveRunWhileTrue) {
            if (this.rerunPending) {
                // Runs the other actions too
                this.rerunPending = false;
                this.regatePending = false;
                this.placements = this.runner.rerun(this.placements);
            } else if (this.regatePending) {
                this.regatePending = false;
                this.runner.runNonPanelActions();
            }
            return;
        }

        console.debug("Running journey actions", this.journey);
        const placements = this.runner.runAction();
        if (placements) {
            this.placements = placements;
            this.haveRunWhileTrue = true;
        }
    }

    // Asked before one of our panels goes back up after the router discarded it (on the next page, say). That stands
    // in for a run, so the condition has to hold now, not just when it was last checked: what it reads may have
    // changed with the page since. It also pays the throttle, and the other actions then run again.
    regate(): boolean {
        // Asked first, without charging it: a throttled panel asks again every scan, and shouldn't run the merchant's
        // condition each time
        if (!this.runner.chargeThrottle(true)) {
            return false;
        }
        if (!this.condition.evaluate()) {
            // A condition that's now false takes the placements down; one not yet known holds the panel back
            this.evaluateDebounced();
            return false;
        }
        this.runner.chargeThrottle();
        // Debounced, so panels that go back up together run the other actions once
        this.regatePending = true;
        this.evaluateDebounced();
        return true;
    }

    // The next time the condition holds, the actions run again and put up fresh placements.
    removePlacements(): void {
        for (const placement of this.placements) {
            placement.remove();
        }
        this.placements = [];
        this.haveRunWhileTrue = false;
        this.rerunPending = false;
        this.regatePending = false;
    }

    cleanup(): void {
        this.cleanupJobs.cleanup();
        this.removePlacements();
        this.condition.cleanup();
    }
}

export function evaluateJourneyPrecondition(journey: Journey): boolean {
    const {precondition} = journey.options;
    if (!precondition) {
        return true;
    }
    const condition = parseConditionNode(journey, precondition as ConditionNodeDescription, NOOP_FUNCTION);
    const result = condition.evaluate();
    condition.cleanup();
    if (result == null) {
        merchantFunctionModule.handleException(
            new Error(`Precondition for journey '${journey.alias}' could not be evaluated synchronously.`),
            journey.alias,
        );
        return false;
    }
    return result;
}

export function evaluateConditionAndRun(journey: Journey): JourneyConditionEvaluator {
    // TODO @branch I'm not sure running the journey's action every time the condition
    //  re-evaluates to true is correct in all cases, think about this some more.
    // TODO @branch @Mihai by default journeys should have a single blocker
    return new JourneyConditionEvaluator(journey);
}
